import { usePageRefreshKey } from '../../../../../context/PageRefreshContext';
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from '@iconify/react';
import { getOpeningBalances, createOpeningBalance, updateOpeningBalance, deleteOpeningBalance, bulkUploadOpeningBalances } from "../../../../../redux/actions/SocietyPortal/OpeningBalanceAction/OpeningBalanceAction";
import { getInvoiceTypes } from "../../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction";
import { getWingsDropdown, getFlatsDropdown } from "../../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction";
import { selectedSocietyStorer } from "../../../../../utils/webStorer";
import axiosInstance from "../../../../../axios/axiosInstance";
import apiEndpoints from "../../../../../axios/api";
import OpeningBalanceModal from "./OpeningBalanceModal";
import FormSelect from "../../../../../components/child/FormSelect";
import { PaginationLimit } from "../../../../../components/PaginationLimit";
import { formatIstDate } from '../../../../../utils/helpers/istDate';

const initialFormData = {
    wing_id: "",
    flat_id: "",
    invoice_type_id: "",
    balance_date: "",
    financial_year: "",
    principal_amount: "",
    interest_amount: "",
    tax_amount: "",
    advance_amount: "",
    narration: "",
    reference_number: "",
    status: "DRAFT",
    remarks: ""
};

const OpeningBalance = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const {
        openingBalancesData,
        openingBalancesPagination,
        loading,
        createLoading,
        updateLoading,
        deleteLoading,
        bulkUploadLoading
    } = useSelector(state => state.OpeningBalanceReducer);
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers);
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers);

    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({ ...initialFormData });
    const [editId, setEditId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);

    // Filter state
    const [filterWingId, setFilterWingId] = useState("");
    const [filterFlatId, setFilterFlatId] = useState("");
    const [filterInvoiceTypeId, setFilterInvoiceTypeId] = useState("");
    const [filterFinancialYear, setFilterFinancialYear] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    // The flat belonging to the row being viewed/edited, merged into the modal's
    // flat options so a shared-slice race can never blank the selection.
    const [selectedFlatOption, setSelectedFlatOption] = useState(null);

    const societyId = selectedSocietyStorer.getSocietyId();
    const [searchParams, setSearchParams] = useState({
        page: 1,
        limit: 10,
        society_id: societyId
    });

    const financialYearOptions = [
        { value: '2023-24', label: '2023-24' },
        { value: '2024-25', label: '2024-25' },
        { value: '2025-26', label: '2025-26' },
        { value: '2026-27', label: '2026-27' }
    ];

    const statusOptions = [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'POSTED', label: 'Posted' },
        { value: 'ADJUSTED', label: 'Adjusted' },
        { value: 'CANCELLED', label: 'Cancelled' }
    ];

    // Fetch initial data
    useEffect(() => {
        if (societyId) {
            dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
            dispatch(getWingsDropdown({ society_id: societyId }));
        }
    }, [dispatch, societyId]);

    // Fetch opening balances
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (societyId) {
            const params = { ...searchParams };
            if (filterFlatId) params.flat_id = filterFlatId;
            if (filterInvoiceTypeId) params.invoice_type_id = filterInvoiceTypeId;
            if (filterFinancialYear) params.financial_year = filterFinancialYear;
            if (filterStatus) params.status = filterStatus;
            dispatch(getOpeningBalances(params));
        }
    }, [dispatch, searchParams, societyId, filterFlatId, filterInvoiceTypeId, filterFinancialYear, filterStatus, refreshKey]);

    // Fetch flats when wing changes in filter
    useEffect(() => {
        if (filterWingId) {
            dispatch(getFlatsDropdown({ wing_id: filterWingId }));
        }
    }, [dispatch, filterWingId]);

    // Fetch flats when wing changes in form
    useEffect(() => {
        if (formData.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: formData.wing_id }));
        }
    }, [dispatch, formData.wing_id]);

    const refreshList = () => {
        const params = { ...searchParams };
        if (filterFlatId) params.flat_id = filterFlatId;
        if (filterInvoiceTypeId) params.invoice_type_id = filterInvoiceTypeId;
        if (filterFinancialYear) params.financial_year = filterFinancialYear;
        if (filterStatus) params.status = filterStatus;
        dispatch(getOpeningBalances(params));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const onSuccess = () => {
            handleCloseModal();
            refreshList();
        };

        if (editId) {
            dispatch(updateOpeningBalance({
                id: editId,
                flat_id: formData.flat_id,
                invoice_type_id: formData.invoice_type_id || undefined,
                balance_date: formData.balance_date,
                financial_year: formData.financial_year,
                principal_amount: parseFloat(formData.principal_amount) || 0,
                interest_amount: parseFloat(formData.interest_amount) || 0,
                tax_amount: parseFloat(formData.tax_amount) || 0,
                advance_amount: parseFloat(formData.advance_amount) || 0,
                narration: formData.narration || undefined,
                reference_number: formData.reference_number || undefined,
                status: formData.status,
                remarks: formData.remarks || undefined,
                onSuccess
            }));
        } else {
            dispatch(createOpeningBalance({
                society_id: societyId,
                flat_id: formData.flat_id,
                invoice_type_id: formData.invoice_type_id || undefined,
                balance_date: formData.balance_date,
                financial_year: formData.financial_year,
                principal_amount: parseFloat(formData.principal_amount) || 0,
                interest_amount: parseFloat(formData.interest_amount) || 0,
                tax_amount: parseFloat(formData.tax_amount) || 0,
                advance_amount: parseFloat(formData.advance_amount) || 0,
                narration: formData.narration || undefined,
                reference_number: formData.reference_number || undefined,
                status: formData.status,
                remarks: formData.remarks || undefined,
                onSuccess
            }));
        }
    };

    const handleEdit = (item) => {
        // flatsDropdown is one shared slice fed by two effects — the list filter's
        // wing and the form's wing — so whichever fetch resolves last wins and the
        // record's own flat can be absent from the options. findAllForDropdown
        // also returns only ACTIVE flats. Carry the row's flat along and let the
        // modal merge it in, so the saved flat always renders.
        setSelectedFlatOption(item.flat || null);
        // Load flats for the wing
        if (item.flat?.wing?.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: item.flat.wing.wing_id }));
        }
        setFormData({
            wing_id: item.flat?.wing?.wing_id || "",
            flat_id: item.flat_id || "",
            invoice_type_id: item.invoice_type_id || "",
            balance_date: item.balance_date ? item.balance_date.split('T')[0] : "",
            financial_year: item.financial_year || "",
            principal_amount: item.principal_amount || "",
            interest_amount: item.interest_amount || "",
            tax_amount: item.tax_amount || "",
            advance_amount: item.advance_amount || "",
            narration: item.narration || "",
            reference_number: item.reference_number || "",
            status: item.status || "DRAFT",
            remarks: item.remarks || ""
        });
        setEditId(item.opening_balance_id);
        setIsReadOnly(false);
        setShowModal(true);
    };

    const handleView = (item) => {
        setSelectedFlatOption(item.flat || null);
        if (item.flat?.wing?.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: item.flat.wing.wing_id }));
        }
        setFormData({
            wing_id: item.flat?.wing?.wing_id || "",
            flat_id: item.flat_id || "",
            invoice_type_id: item.invoice_type_id || "",
            balance_date: item.balance_date ? item.balance_date.split('T')[0] : "",
            financial_year: item.financial_year || "",
            principal_amount: item.principal_amount || "",
            interest_amount: item.interest_amount || "",
            tax_amount: item.tax_amount || "",
            advance_amount: item.advance_amount || "",
            narration: item.narration || "",
            reference_number: item.reference_number || "",
            status: item.status || "DRAFT",
            remarks: item.remarks || ""
        });
        setEditId(item.opening_balance_id);
        setIsReadOnly(true);
        setShowModal(true);
    };

    // Bulk-uploaded rows land as DRAFT, and posting them previously meant opening
    // each one's edit modal and switching the status field by hand. This posts
    // straight from the list, reusing the same update endpoint.
    const handlePost = (item) => {
        if (!window.confirm(`Post the opening balance for flat ${item.flat?.flat_number || item.flat_id}? Posted balances can no longer be deleted.`)) return;
        dispatch(updateOpeningBalance({
            id: item.opening_balance_id,
            status: 'POSTED',
            onSuccess: () => refreshList()
        }));
    };

    const handleDelete = (item) => {
        if (item.status === 'POSTED') {
            return alert("Cannot delete a posted opening balance. Cancel it first.");
        }
        if (window.confirm("Are you sure you want to delete this opening balance?")) {
            dispatch(deleteOpeningBalance({
                id: item.opening_balance_id,
                onSuccess: () => refreshList()
            }));
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({ ...initialFormData });
        setEditId(null);
        setIsReadOnly(false);
    };

    const [templateLoading, setTemplateLoading] = useState(false);

    // Fetches the blank template as a blob and triggers a browser download.
    // Kept inline (no saga) — this is a one-shot binary fetch with no state
    // to share; going through redux would just add ceremony for no gain.
    const handleDownloadTemplate = async () => {
        if (templateLoading) return;
        setTemplateLoading(true);
        try {
            // society_id lets the template show this society's billing type titles.
            const res = await axiosInstance.get(apiEndpoints.DOWNLOAD_OPENING_BALANCE_TEMPLATE_API, {
                params: { society_id: societyId },
                responseType: 'blob'
            });
            // axiosInstance's response interceptor returns response.data by
            // default, so `res` is already the Blob here (not an axios response).
            const blob = res instanceof Blob
                ? res
                : new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'opening_balances_template.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to download template');
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleBulkUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('society_id', societyId);
        dispatch(bulkUploadOpeningBalances({
            formData: uploadFormData,
            onSuccess: () => {
                refreshList();
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        }));
    };

    const handleClearFilters = () => {
        setFilterWingId("");
        setFilterFlatId("");
        setFilterInvoiceTypeId("");
        setFilterFinancialYear("");
        setFilterStatus("");
        setSearchParams({ page: 1, limit: 10, society_id: societyId });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'DRAFT':
                return <span className="badge bg-warning-50 text-warning-600 px-12 py-6 radius-4 fw-medium">Draft</span>;
            case 'POSTED':
                return <span className="badge bg-success-50 text-success-600 px-12 py-6 radius-4 fw-medium">Posted</span>;
            case 'ADJUSTED':
                return <span className="badge bg-info-50 text-info-600 px-12 py-6 radius-4 fw-medium">Adjusted</span>;
            case 'CANCELLED':
                return <span className="badge bg-danger-50 text-danger-600 px-12 py-6 radius-4 fw-medium">Cancelled</span>;
            default:
                return <span className="badge bg-neutral-100 text-secondary-light px-12 py-6 radius-4 fw-medium">{status}</span>;
        }
    };

    const formatAmount = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toFixed(2);
    };

    const hasActiveFilters = filterFlatId || filterInvoiceTypeId || filterFinancialYear || filterStatus;

    const invoiceTypeOptions = invoiceTypesData?.map(type => ({
        value: type.invoice_type_id,
        label: type.invoice_title
    })) || [];

    return (
        <div className="invoice-setup-page">
            {/* Page Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-12">
                <div>
                    <h6 className="fw-semibold mb-0 text-base">Opening Balances</h6>
                    <p className="text-secondary-light text-sm mb-0 mt-1">
                        Manage opening balances for member accounts
                    </p>
                    <p className="text-secondary-light text-xs mb-0 mt-1">
                        Posting a balance books an opening journal (Dr member A/C, Cr Opening Balance Adjustment A/C).
                        Don&apos;t also enter these members&apos; balances in Accounts → Opening, or they&apos;ll be counted twice.
                    </p>
                </div>
            </div>

            {/* Info Banner */}
            <div className="card radius-8 border-0 mb-16 bg-primary-50">
                <div className="card-body p-12 d-flex align-items-center gap-3">
                    <div className="w-40-px h-40-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center flex-shrink-0">
                        <Icon icon="mdi:information-outline" className="text-primary-600 text-base" />
                    </div>
                    <p className="text-xs text-secondary-light mb-0">
                        <strong className="text-primary-600">Note:</strong> Opening balances represent outstanding dues or advance payments carried forward from a previous system.
                        Positive total amounts indicate the member owes the society, while negative amounts indicate advance/credit balance.
                    </p>
                </div>
            </div>

            {/* Action Bar */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-12">
                <div className="d-flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            setFormData({ ...initialFormData });
                            setEditId(null);
                            setIsReadOnly(false);
                            setShowModal(true);
                        }}
                        className="btn d-flex align-items-center gap-2 px-10 py-4 radius-6 text-white"
                        style={{ backgroundColor: '#f59e0b' }}
                        disabled={loading}
                    >
                        <Icon icon="mdi:plus-circle" className="text-base" />
                        Add Opening Balance
                    </button>
                    <button
                        type="button"
                        onClick={handleDownloadTemplate}
                        disabled={templateLoading}
                        className="btn btn-outline-info d-flex align-items-center gap-2 px-14 py-8 radius-8"
                    >
                        <Icon
                            icon={templateLoading ? "mdi:loading" : "mdi:file-download-outline"}
                            className={`text-base ${templateLoading ? 'mdi-spin' : ''}`}
                        />
                        {templateLoading ? 'Preparing...' : 'Download Template'}
                    </button>
                    <label
                        className="btn btn-outline-primary d-flex align-items-center gap-2 px-14 py-8 radius-8 mb-0"
                        style={{ cursor: bulkUploadLoading ? 'not-allowed' : 'pointer' }}
                    >
                        <Icon icon={bulkUploadLoading ? "mdi:loading" : "mdi:file-upload-outline"} className={`text-base ${bulkUploadLoading ? 'mdi-spin' : ''}`} />
                        {bulkUploadLoading ? 'Uploading...' : 'Bulk Upload'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xls,.xlsx"
                            className="d-none"
                            onChange={handleBulkUpload}
                            disabled={bulkUploadLoading}
                        />
                    </label>
                </div>
            </div>

            {/* Filters */}
            <div className="card radius-12 border-0 shadow-sm mb-16">
                <div className="card-body p-20">
                    <div className="d-flex align-items-center justify-content-between mb-16">
                        <div className="d-flex align-items-center gap-2">
                            <Icon icon="mdi:filter-variant" className="text-base text-secondary-light" />
                            <span className="fw-semibold text-sm">Filters</span>
                        </div>
                        {hasActiveFilters && (
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            >
                                <Icon icon="mdi:close" className="text-sm" />
                                Clear
                            </button>
                        )}
                    </div>
                    <div className="row gy-3">
                        <div className="col-lg-3 col-md-6">
                            <FormSelect
                                name="filter_wing"
                                value={filterWingId}
                                onChange={(e) => {
                                    setFilterWingId(e.target.value);
                                    setFilterFlatId("");
                                }}
                                options={wingsDropdown || []}
                                valueKey="wing_id"
                                labelKey="title"
                                placeholder="All Wings"
                            />
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <FormSelect
                                name="filter_flat"
                                value={filterFlatId}
                                onChange={(e) => setFilterFlatId(e.target.value)}
                                options={flatsDropdown || []}
                                valueKey="flat_id"
                                labelKey="flat_number"
                                placeholder="All Flats"
                                disabled={!filterWingId}
                            />
                        </div>
                        <div className="col-lg-2 col-md-4">
                            <FormSelect
                                name="filter_invoice_type"
                                value={filterInvoiceTypeId}
                                onChange={(e) => setFilterInvoiceTypeId(e.target.value)}
                                options={invoiceTypeOptions}
                                placeholder="All Types"
                            />
                        </div>
                        <div className="col-lg-2 col-md-4">
                            <FormSelect
                                name="filter_financial_year"
                                value={filterFinancialYear}
                                onChange={(e) => setFilterFinancialYear(e.target.value)}
                                options={financialYearOptions}
                                valueKey="value"
                                labelKey="label"
                                placeholder="All FY"
                            />
                        </div>
                        <div className="col-lg-2 col-md-4">
                            <FormSelect
                                name="filter_status"
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                options={statusOptions}
                                valueKey="value"
                                labelKey="label"
                                placeholder="All Status"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {/* Data Table */}
            {!loading && (
                <div className="card radius-12 border-0 shadow-sm">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="bg-neutral-50">
                                    <tr>
                                        <th className="ps-20 py-8 text-secondary-light text-xs fw-semibold">#</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Flat</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Invoice Type</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">FY</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Balance Date</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Principal</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Interest</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Tax</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Advance</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold text-end">Total</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Status</th>
                                        <th className="py-8 text-secondary-light text-xs fw-semibold">Source</th>
                                        <th className="pe-20 py-8 text-secondary-light text-xs fw-semibold text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {openingBalancesData?.length > 0 ? (
                                        openingBalancesData.map((item, index) => {
                                            const canEdit = item.status === 'DRAFT' || item.status === 'POSTED';
                                            const canDelete = item.status === 'DRAFT';
                                            const pageOffset = ((searchParams.page - 1) * searchParams.limit);

                                            return (
                                                <tr key={item.opening_balance_id}>
                                                    <td className="ps-12 py-8 align-middle">
                                                        <span className="text-secondary-light fw-medium">
                                                            {String(pageOffset + index + 1).padStart(2, '0')}
                                                        </span>
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        <div>
                                                            <span className="fw-medium">
                                                                {item.flat?.wing?.title}-{item.flat?.flat_number}
                                                            </span>
                                                            {item.flat?.floor_number && (
                                                                <small className="text-secondary-light d-block">
                                                                    Floor {item.flat.floor_number}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        <span className="text-sm">
                                                            {item.invoiceType?.invoice_title || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        <span className="badge bg-neutral-100 text-secondary-light px-8 py-4 radius-4 text-xs">
                                                            {item.financial_year}
                                                        </span>
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        <span className="text-sm">
                                                            {item.balance_date ? formatIstDate(item.balance_date) : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="py-16 align-middle text-end">
                                                        <span className="fw-medium">₹{formatAmount(item.principal_amount)}</span>
                                                    </td>
                                                    <td className="py-16 align-middle text-end">
                                                        <span className="text-sm">₹{formatAmount(item.interest_amount)}</span>
                                                    </td>
                                                    <td className="py-16 align-middle text-end">
                                                        <span className="text-sm">₹{formatAmount(item.tax_amount)}</span>
                                                    </td>
                                                    <td className="py-16 align-middle text-end">
                                                        <span className="text-sm text-success-600">₹{formatAmount(item.advance_amount)}</span>
                                                    </td>
                                                    <td className="py-16 align-middle text-end">
                                                        <span className={`fw-bold ${parseFloat(item.total_amount) >= 0 ? 'text-danger-600' : 'text-success-600'}`}>
                                                            ₹{formatAmount(item.total_amount)}
                                                        </span>
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        {getStatusBadge(item.status)}
                                                    </td>
                                                    <td className="py-16 align-middle">
                                                        <span className="badge bg-neutral-100 text-secondary-light px-8 py-4 radius-4 text-xs">
                                                            {item.source || 'MANUAL'}
                                                        </span>
                                                    </td>
                                                    <td className="pe-12 py-8 align-middle text-end">
                                                        <div className="d-flex align-items-center justify-content-end gap-1">
                                                            <button
                                                                onClick={() => handleView(item)}
                                                                className="btn btn-sm btn-outline-info d-flex align-items-center justify-content-center"
                                                                style={{ width: '32px', height: '32px' }}
                                                                title="View"
                                                            >
                                                                <Icon icon="mdi:eye-outline" className="text-lg" />
                                                            </button>
                                                            {canEdit && (
                                                                <button
                                                                    onClick={() => handleEdit(item)}
                                                                    className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                                                                    style={{ width: '32px', height: '32px' }}
                                                                    title="Edit"
                                                                >
                                                                    <Icon icon="mdi:pencil-outline" className="text-lg" />
                                                                </button>
                                                            )}
                                                            {canEdit && item.status === 'DRAFT' && (
                                                                <button
                                                                    onClick={() => handlePost(item)}
                                                                    className="btn btn-sm btn-outline-success d-flex align-items-center justify-content-center"
                                                                    style={{ width: '32px', height: '32px' }}
                                                                    title="Post"
                                                                >
                                                                    <Icon icon="mdi:check-circle-outline" className="text-lg" />
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button
                                                                    onClick={() => handleDelete(item)}
                                                                    className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                                                                    style={{ width: '32px', height: '32px' }}
                                                                    title="Delete"
                                                                    disabled={deleteLoading}
                                                                >
                                                                    <Icon icon="mdi:trash-can-outline" className="text-lg" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="13" className="text-center py-40">
                                                <div className="w-80-px h-80-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center mx-auto mb-20">
                                                    <Icon icon="mdi:file-document-outline" className="text-secondary-light text-2xl" />
                                                </div>
                                                <h6 className="fw-semibold mb-2">No Opening Balances Found</h6>
                                                <p className="text-secondary-light text-sm mb-20">
                                                    {hasActiveFilters
                                                        ? 'No records match your filters. Try adjusting the filter criteria.'
                                                        : 'Get started by adding your first opening balance entry.'}
                                                </p>
                                                {!hasActiveFilters && (
                                                    <button
                                                        onClick={() => {
                                                            setFormData({ ...initialFormData });
                                                            setEditId(null);
                                                            setIsReadOnly(false);
                                                            setShowModal(true);
                                                        }}
                                                        className="btn btn-primary d-inline-flex align-items-center gap-2 px-20 py-10 radius-8"
                                                    >
                                                        <Icon icon="mdi:plus-circle" className="text-base" />
                                                        Add First Opening Balance
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {openingBalancesPagination && openingBalancesPagination.totalPages > 1 && (
                            <div className="p-20 border-top">
                                <PaginationLimit
                                    pagination={openingBalancesPagination}
                                    searchParams={searchParams}
                                    setSearchParams={setSearchParams}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Styles */}
            <style>{`
                .modal-backdrop-custom {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    animation: fadeIn 0.2s ease;
                }

                .modal-content-custom {
                    width: 100%;
                    max-width: 640px;
                    margin: 16px;
                    animation: slideUp 0.3s ease;
                    max-height: 90vh;
                    overflow-y: auto;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>

            {/* Modal */}
            <OpeningBalanceModal
                show={showModal}
                handleClose={handleCloseModal}
                handleSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                editId={editId}
                wingsDropdown={wingsDropdown}
                flatsDropdown={flatsDropdown}
                selectedFlatOption={selectedFlatOption}
                invoiceTypesData={invoiceTypesData}
                loading={editId ? updateLoading : createLoading}
                isReadOnly={isReadOnly}
            />
        </div>
    );
};

export default OpeningBalance;
